/**
 * The tones a vfx is drawn in (ADR-134 decision 4): one colour in, four out, so any tint gives a matching burst. With no
 * tint the tones are the game's gold (`--ot-tier-gold`, its light and its dark), written out as the design has them; with
 * one, the mid tone is the tint, the light is the tint half way to white, the dark is the tint 30 percent toward black and
 * the core is the tint most of the way to white. All as 0xRRGGBB.
 */

/**
 * The four tones.
 */
export interface Tones {
  readonly mid: number;
  readonly light: number;
  readonly dark: number;
  readonly core: number;
}

/**
 * The default tones: the game's gold.
 */
export const GOLD_TONES: Tones = Object.freeze({ mid: 0xf2c848, light: 0xffec96, dark: 0xc88c28, core: 0xfffff0 });

/**
 * The tint meaning "none" in the feed's typed arrays.
 */
export const NO_TINT = -1;

/**
 * A colour a share of the way toward another.
 *
 * @param from The colour moved from, 0xRRGGBB.
 * @param to The colour moved toward.
 * @param share How far, 0 to 1.
 * @returns The colour.
 */
function mix(from: number, to: number, share: number): number {
  let out = 0;
  for (let shift = 16; shift >= 0; shift -= 8) {
    const a = (from >> shift) & 0xff;
    const b = (to >> shift) & 0xff;
    out |= Math.round(a + (b - a) * share) << shift;
  }
  return out;
}

/**
 * The tones of a tint.
 *
 * @param tint The tint as 0xRRGGBB, or `NO_TINT` or undefined for the game's gold.
 * @returns The tones.
 * @example
 * tonesOf(0xd04040).mid;
 * // 0xd04040
 */
export function tonesOf(tint?: number): Tones {
  if (tint === undefined || tint < 0) {
    return GOLD_TONES;
  }
  const colour = tint & 0xffffff;
  return { mid: colour, light: mix(colour, 0xffffff, 0.5), dark: mix(colour, 0x000000, 0.3), core: mix(colour, 0xffffff, 0.85) };
}
